/**
 * Usage Tracking for Anonymous Users
 *
 * Tracks calculator/tool usage via cookies for analytics purposes.
 * No limits enforced - all users have unlimited access.
 *
 * Used to:
 * - Show newsletter signup prompt after 2nd use
 * - Track popular tools for SEO prioritization
 * - Understand user engagement patterns
 */

import Cookies from 'js-cookie'

// Cookie name for usage tracking
const USAGE_COOKIE_NAME = 'wgit_usage'

// Daily limit for anonymous users (generous but encourages signup)
const ANONYMOUS_DAILY_LIMIT = 5

export interface UsageData {
  count: number
  firstUsedAt: string // ISO timestamp
  lastUsedAt: string // ISO timestamp
  toolsUsed: string[] // Track which tools were used
}

/**
 * Get current usage data from cookie
 */
export const getUsageData = (): UsageData | null => {
  if (typeof window === 'undefined') return null

  const cookieData = Cookies.get(USAGE_COOKIE_NAME)
  if (!cookieData) return null

  try {
    return JSON.parse(cookieData) as UsageData
  } catch {
    // Invalid cookie data, reset
    Cookies.remove(USAGE_COOKIE_NAME)
    return null
  }
}

/**
 * Check if usage data is from today
 */
const isToday = (timestamp: string): boolean => {
  const date = new Date(timestamp)
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 * Reset usage data (new day or invalid data)
 */
const resetUsageData = (): UsageData => {
  const now = new Date().toISOString()
  const newData: UsageData = {
    count: 0,
    firstUsedAt: now,
    lastUsedAt: now,
    toolsUsed: []
  }

  // Set cookie to expire at midnight tonight
  const tomorrow = new Date()
  tomorrow.setHours(24, 0, 0, 0)

  Cookies.set(USAGE_COOKIE_NAME, JSON.stringify(newData), {
    expires: tomorrow,
    sameSite: 'lax'
  })

  return newData
}

/**
 * Increment usage count for a tool
 */
export const incrementUsage = (toolName: string): UsageData => {
  let usageData = getUsageData()

  // Reset if no data or data is from previous day
  if (!usageData || !isToday(usageData.firstUsedAt)) {
    usageData = resetUsageData()
  }

  // Increment count
  const now = new Date().toISOString()
  const updatedData: UsageData = {
    count: usageData.count + 1,
    firstUsedAt: usageData.firstUsedAt,
    lastUsedAt: now,
    toolsUsed: [...new Set([...usageData.toolsUsed, toolName])] // Dedupe
  }

  // Update cookie (expires at midnight)
  const tomorrow = new Date()
  tomorrow.setHours(24, 0, 0, 0)

  Cookies.set(USAGE_COOKIE_NAME, JSON.stringify(updatedData), {
    expires: tomorrow,
    sameSite: 'lax'
  })

  return updatedData
}

/**
 * Check if user has reached their daily limit
 * NOTE: Always returns false - no limits enforced anymore
 * Kept for backward compatibility
 */
export const hasReachedLimit = (isAuthenticated: boolean): boolean => {
  // No limits enforced - everyone has unlimited access
  return false
}

/**
 * Get remaining uses for today
 * NOTE: Always returns null (unlimited) - no limits enforced anymore
 * Kept for backward compatibility
 */
export const getRemainingUses = (isAuthenticated: boolean): number | null => {
  // Everyone has unlimited access
  return null
}

/**
 * Get usage percentage (for progress bars)
 */
export const getUsagePercentage = (isAuthenticated: boolean): number => {
  if (isAuthenticated) return 0 // No limit to display

  const usageData = getUsageData()

  if (!usageData || !isToday(usageData.firstUsedAt)) return 0

  return Math.min(100, (usageData.count / ANONYMOUS_DAILY_LIMIT) * 100)
}

/**
 * Clear usage data (for testing or manual reset)
 */
export const clearUsageData = (): void => {
  Cookies.remove(USAGE_COOKIE_NAME)
}

/**
 * Get time until reset (midnight tonight)
 */
export const getTimeUntilReset = (): string => {
  const now = new Date()
  const midnight = new Date()
  midnight.setHours(24, 0, 0, 0)

  const diffMs = midnight.getTime() - now.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`
  }
  return `${diffMinutes}m`
}

/**
 * Constants for external use
 */
export const USAGE_LIMITS = {
  ANONYMOUS_DAILY: ANONYMOUS_DAILY_LIMIT,
  FREE_DAILY: null, // Unlimited for free accounts
  PREMIUM_DAILY: null // Unlimited for premium
} as const

// ============================================
// VISIT COUNTING (localStorage - persists across sessions)
// ============================================

const VISIT_COUNT_KEY = 'wgit_visit_count'
const NEWSLETTER_DISMISSED_KEY = 'wgit_newsletter_dismissed'
const LAST_VISIT_KEY = 'wgit_last_visit'

interface VisitData {
  count: number
  firstVisitAt: string
  lastVisitAt: string
}

/**
 * Get visit count (persists across sessions via localStorage)
 * Returns the number of times the user has visited the site
 */
export const getVisitCount = (): number => {
  if (typeof window === 'undefined') return 0

  try {
    const data = localStorage.getItem(VISIT_COUNT_KEY)
    if (!data) return 0

    const visitData = JSON.parse(data) as VisitData
    return visitData.count || 0
  } catch {
    return 0
  }
}

/**
 * Increment visit count
 * Should be called once per session (on initial page load)
 * Uses session storage to prevent counting multiple page views in same session
 */
export const incrementVisitCount = (): number => {
  if (typeof window === 'undefined') return 0

  // Check if already counted this session
  const sessionCounted = sessionStorage.getItem('wgit_visit_counted')
  if (sessionCounted === 'true') {
    return getVisitCount()
  }

  try {
    const now = new Date().toISOString()
    let visitData: VisitData

    const existingData = localStorage.getItem(VISIT_COUNT_KEY)
    if (existingData) {
      visitData = JSON.parse(existingData) as VisitData
      visitData.count += 1
      visitData.lastVisitAt = now
    } else {
      visitData = {
        count: 1,
        firstVisitAt: now,
        lastVisitAt: now
      }
    }

    localStorage.setItem(VISIT_COUNT_KEY, JSON.stringify(visitData))

    // Mark as counted for this session
    sessionStorage.setItem('wgit_visit_counted', 'true')

    return visitData.count
  } catch {
    return 1
  }
}

/**
 * Check if user has at least one tracked visit
 * (Previously required 2+ visits, but this blocked 90%+ of organic search
 * traffic who are first-time visitors and never return)
 */
export const isReturningVisitor = (): boolean => {
  return getVisitCount() >= 1
}

const NEWSLETTER_SUBSCRIBED_KEY = 'wgit_newsletter_subscribed'

/**
 * How long a modal dismissal suppresses future asks. Subscribing suppresses
 * forever. One day matches the popup-tool consensus (show again after 1 day
 * or per session when the visitor has not submitted); a dismisser on a new
 * visit is a returning reader, not a colder one.
 */
const NEWSLETTER_DISMISS_COOLDOWN_DAYS = 1

/**
 * Check if newsletter asks are currently suppressed.
 *
 * Two suppression states:
 * - Subscribed (any capture surface succeeded): permanent.
 * - Dismissed (modal closed without subscribing): 30-day cooldown, after which
 *   the visitor becomes eligible again. Dismissal used to be permanent, which
 *   silenced the modal forever after a single close; a cooldown recovers those
 *   visitors without re-asking anyone who actually converted.
 *
 * Legacy migration: the old value was the literal string 'true' (permanent,
 * and set on BOTH dismissal and subscribe — the states were conflated). We
 * can't tell which it was, so 'true' is migrated to a dismissal timestamped
 * now: nobody gets modal-bombed today, and the cooldown re-opens eligibility
 * in 30 days.
 */
export const isNewsletterDismissed = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    if (localStorage.getItem(NEWSLETTER_SUBSCRIBED_KEY) === 'true') return true

    const raw = localStorage.getItem(NEWSLETTER_DISMISSED_KEY)
    if (!raw) return false

    if (raw === 'true') {
      // Legacy permanent flag — restart as a fresh 30-day cooldown
      localStorage.setItem(NEWSLETTER_DISMISSED_KEY, new Date().toISOString())
      return true
    }

    const dismissedAt = Date.parse(raw)
    if (Number.isNaN(dismissedAt)) return false

    const cooldownMs = NEWSLETTER_DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
    return Date.now() - dismissedAt < cooldownMs
  } catch {
    return false
  }
}

/**
 * Dismiss the newsletter modal (user closed without subscribing).
 * Suppresses all newsletter asks for NEWSLETTER_DISMISS_COOLDOWN_DAYS.
 */
export const dismissNewsletter = (): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(NEWSLETTER_DISMISSED_KEY, new Date().toISOString())
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Mark the visitor as subscribed (a capture surface succeeded).
 * Permanently suppresses the newsletter modal on this browser.
 */
export const markNewsletterSubscribed = (): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(NEWSLETTER_SUBSCRIBED_KEY, 'true')
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Reset newsletter dismissal + subscribed flags (for testing)
 */
export const resetNewsletterDismissal = (): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(NEWSLETTER_DISMISSED_KEY)
    localStorage.removeItem(NEWSLETTER_SUBSCRIBED_KEY)
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get visit data for analytics
 */
export const getVisitData = (): VisitData | null => {
  if (typeof window === 'undefined') return null

  try {
    const data = localStorage.getItem(VISIT_COUNT_KEY)
    if (!data) return null
    return JSON.parse(data) as VisitData
  } catch {
    return null
  }
}
